import { SelectOption } from './InputTypes';

export type SelectOptionGroup = {
    label: string;
    options: SelectOption[];
};

export const DEFAULT_TIME_ZONE_ID = 'America/New_York';

/** US zones, operational ones first — the rest of the country stays available but out of the way. */
const US_ZONES = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Detroit',
    'America/Indiana/Indianapolis',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Vevay',
    'America/Indiana/Tell_City',
    'America/Indiana/Knox',
    'America/Menominee',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/North_Dakota/Beulah',
    'America/Boise',
    'America/Juneau',
    'America/Sitka',
    'America/Metlakatla',
    'America/Yakutat',
    'America/Nome',
    'America/Adak',
    'America/Puerto_Rico',
    'Pacific/Guam',
    'Pacific/Pago_Pago',
];

const CANADA_ZONES = [
    'America/Toronto',
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Halifax',
    'America/St_Johns',
    'America/Regina',
    'America/Moncton',
    'America/Glace_Bay',
    'America/Goose_Bay',
    'America/Blanc-Sablon',
    'America/Iqaluit',
    'America/Resolute',
    'America/Rankin_Inlet',
    'America/Swift_Current',
    'America/Cambridge_Bay',
    'America/Inuvik',
    'America/Creston',
    'America/Dawson_Creek',
    'America/Fort_Nelson',
    'America/Whitehorse',
    'America/Dawson',
    'America/Atikokan',
];

const MEXICO_ZONES = [
    'America/Mexico_City',
    'America/Tijuana',
    'America/Monterrey',
    'America/Cancun',
    'America/Merida',
    'America/Matamoros',
    'America/Chihuahua',
    'America/Ciudad_Juarez',
    'America/Ojinaga',
    'America/Mazatlan',
    'America/Bahia_Banderas',
    'America/Hermosillo',
];

/**
 * Used when the browser predates Intl.supportedValuesOf, so the picker still covers
 * North America plus the zones a partner integration realistically lands in.
 */
const FALLBACK_WORLD_ZONES = [
    'UTC',
    'America/Sao_Paulo',
    'America/Bogota',
    'America/Lima',
    'America/Santiago',
    'America/Argentina/Buenos_Aires',
    'America/Panama',
    'America/Guatemala',
    'Europe/London',
    'Europe/Dublin',
    'Europe/Lisbon',
    'Europe/Madrid',
    'Europe/Paris',
    'Europe/Amsterdam',
    'Europe/Berlin',
    'Europe/Rome',
    'Europe/Zurich',
    'Europe/Prague',
    'Europe/Warsaw',
    'Europe/Stockholm',
    'Europe/Helsinki',
    'Europe/Kyiv',
    'Europe/Bucharest',
    'Europe/Athens',
    'Europe/Istanbul',
    'Europe/Moscow',
    'Africa/Casablanca',
    'Africa/Lagos',
    'Africa/Cairo',
    'Africa/Johannesburg',
    'Africa/Nairobi',
    'Asia/Jerusalem',
    'Asia/Dubai',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Dhaka',
    'Asia/Bangkok',
    'Asia/Jakarta',
    'Asia/Singapore',
    'Asia/Hong_Kong',
    'Asia/Shanghai',
    'Asia/Manila',
    'Asia/Seoul',
    'Asia/Tokyo',
    'Australia/Perth',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Melbourne',
    'Australia/Sydney',
    'Pacific/Auckland',
    'Pacific/Fiji',
];

/**
 * ICU has no short abbreviation for these, so it falls back to a city name or a raw
 * offset. Ops reads the label on dashboards, so spell out the ones we actually ship to.
 */
const LABEL_OVERRIDES: Record<string, string> = {
    'America/St_Johns': 'NT',
    // Yukon and Mexico's Pacific coast sit on Mountain Standard Time all year.
    'America/Whitehorse': 'MST',
    'America/Dawson': 'MST',
    'America/Mazatlan': 'MST',
    'America/Hermosillo': 'MST',
    'Pacific/Guam': 'ChST',
    'Pacific/Saipan': 'ChST',
    'Pacific/Pago_Pago': 'SST',
    UTC: 'UTC',
    'Etc/UTC': 'UTC',
    'Etc/GMT': 'UTC',
};

const isCanonicalAbbreviation = (value: string) => /^[A-Z]{2,5}$/.test(value);

const timeZoneNamePart = (timeZoneId: string, style: 'short' | 'shortGeneric' | 'longGeneric'): string | null => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZoneId,
            // TS 4.4's lib predates the generic timeZoneName styles, but ICU has supported them for years.
            timeZoneName: style as 'short',
        }).formatToParts(new Date());
        return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
    } catch {
        return null;
    }
};

/** Minutes east of UTC for `timeZoneId` at `at`, so the picker can show live offsets. */
export const getUtcOffsetMinutes = (timeZoneId: string, at: Date = new Date()): number | null => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZoneId,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).formatToParts(at);

        const lookup = (type: string) => Number(parts.find((part) => part.type === type)?.value);
        const asUtc = Date.UTC(
            lookup('year'),
            lookup('month') - 1,
            lookup('day'),
            lookup('hour'),
            lookup('minute'),
            lookup('second')
        );
        // Wall-clock read back as UTC minus real UTC is exactly the zone's offset.
        return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
    } catch {
        return null;
    }
};

export const formatUtcOffset = (timeZoneId: string, at: Date = new Date()): string => {
    const minutes = getUtcOffsetMinutes(timeZoneId, at);
    if (minutes === null) return '';

    const sign = minutes < 0 ? '−' : '+';
    const absolute = Math.abs(minutes);
    const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
    const rest = String(absolute % 60).padStart(2, '0');
    return `UTC${sign}${hours}:${rest}`;
};

/**
 * The short label ops recognises ("ET", "CT", "MST"), derived from the IANA id so nobody
 * has to keep the two fields in sync by hand. Falls back to the offset when the zone has
 * no accepted abbreviation (most of Europe and Asia).
 */
export const deriveTimeZoneLabel = (timeZoneId?: string | null): string => {
    if (!timeZoneId) return '';
    if (LABEL_OVERRIDES[timeZoneId]) return LABEL_OVERRIDES[timeZoneId];

    const generic = timeZoneNamePart(timeZoneId, 'shortGeneric');
    if (generic && isCanonicalAbbreviation(generic)) return generic;

    const short = timeZoneNamePart(timeZoneId, 'short');
    if (short && isCanonicalAbbreviation(short)) return short;

    return formatUtcOffset(timeZoneId);
};

/** Current wall-clock time in the zone, e.g. "2:14 AM" — a sanity check while picking. */
export const formatTimeZoneNow = (timeZoneId: string, at: Date = new Date()): string => {
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: timeZoneId,
            hour: 'numeric',
            minute: '2-digit',
        }).format(at);
    } catch {
        return '';
    }
};

export const isValidTimeZoneId = (timeZoneId?: string | null): boolean => {
    if (!timeZoneId) return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId }).format(new Date());
        return true;
    } catch {
        return false;
    }
};

/**
 * ICU reports some zones under an older name ("America/Indiana/Indianapolis" resolves to
 * "America/Indianapolis"), so compare canonical forms before deciding a zone is new.
 */
const canonicalId = (timeZoneId: string): string => {
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId }).resolvedOptions().timeZone;
    } catch {
        return timeZoneId;
    }
};

/** "America/Indiana/Knox" -> "Knox, Indiana"; "America/New_York" -> "New York". */
const cityName = (timeZoneId: string): string => {
    const segments = timeZoneId.split('/').slice(1).map((segment) => segment.replace(/_/g, ' '));
    if (segments.length === 0) return timeZoneId;
    return segments.reverse().join(', ');
};

export const getAllTimeZoneIds = (): string[] => {
    const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
        .supportedValuesOf;

    if (typeof supportedValuesOf === 'function') {
        try {
            const ids = supportedValuesOf('timeZone');
            if (ids && ids.length) return ids;
        } catch {
            // fall through to the bundled list
        }
    }

    return [...US_ZONES, ...CANADA_ZONES, ...MEXICO_ZONES, ...FALLBACK_WORLD_ZONES];
};

const REGION_GROUPS: { label: string; matches: (timeZoneId: string) => boolean }[] = [
    { label: 'Americas — other', matches: (id) => id.startsWith('America/') },
    { label: 'Europe', matches: (id) => id.startsWith('Europe/') },
    { label: 'Asia', matches: (id) => id.startsWith('Asia/') },
    { label: 'Australia & Pacific', matches: (id) => id.startsWith('Australia/') || id.startsWith('Pacific/') },
    { label: 'Africa', matches: (id) => id.startsWith('Africa/') },
    { label: 'Atlantic, Indian & other', matches: () => true },
];

const toOption = (timeZoneId: string, at: Date): SelectOption => {
    const offset = formatUtcOffset(timeZoneId, at);
    const label = deriveTimeZoneLabel(timeZoneId);
    const suffix = [offset, label && label !== offset ? label : null].filter(Boolean).join(' · ');
    return {
        value: timeZoneId,
        label: suffix ? `${cityName(timeZoneId)} · ${suffix}` : cityName(timeZoneId),
    };
};

/**
 * Grouped options for the time-zone combobox: US, Canada, Mexico, then the rest of the
 * world. `currentValue` is appended as its own group when it is a zone we do not list
 * (a legacy alias such as "US/Eastern"), so editing an old market never silently drops it.
 */
export const buildTimeZoneOptionGroups = (currentValue?: string | null, at: Date = new Date()): SelectOptionGroup[] => {
    const curated = [
        { label: 'United States', ids: US_ZONES },
        { label: 'Canada', ids: CANADA_ZONES },
        { label: 'Mexico', ids: MEXICO_ZONES },
    ];

    const seen = new Set<string>();
    const remember = (id: string) => {
        seen.add(id);
        seen.add(canonicalId(id));
    };

    const groups: SelectOptionGroup[] = curated.map(({ label, ids }) => {
        ids.forEach(remember);
        return { label, options: ids.map((id) => toOption(id, at)) };
    });

    const remaining = getAllTimeZoneIds();
    REGION_GROUPS.forEach(({ label, matches }) => {
        const ids = remaining.filter((id) => !seen.has(id) && matches(id));
        if (!ids.length) return;
        ids.forEach(remember);
        groups.push({
            label,
            options: ids.map((id) => toOption(id, at)).sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
        });
    });

    if (currentValue && !seen.has(currentValue)) {
        groups.unshift({
            label: 'Currently saved',
            options: [toOption(currentValue, at)],
        });
    }

    return groups;
};
